import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';
import { radioOutcomeSession } from './radioParticipationIntent';
import {
  buildRadioFeedbackPayload,
  getFeedbackUnlockProgress,
  getRadioFeedbackTrackId,
  initialFeedbackForm,
  isFeedbackComplete,
  isFeedbackUnlocked,
} from './hitLabRadioFeedback';

const emptyDraft = () => ({
  form: initialFeedbackForm,
  evidence: { elapsed: 0, duration: 0 },
  submitting: false,
  result: null,
  error: '',
  authRequired: false,
});

const feedbackErrorMessage = (error) => {
  if (error?.response?.status === 401) {
    return 'Your sign-in session expired. Sign in again to submit feedback.';
  }
  const message = error?.response?.data?.message;
  if (message === 'Feedback route is not supported.') {
    return 'Feedback could not be sent from this page. Your answers are still here. Please try again.';
  }
  return message ||
    'Feedback could not be confirmed. Your answers are still here. Please try again.';
};

// Owned by the shared sheet's keyed player session. Nothing persists to storage.
export default function useRadioFeedbackDraft({
  open,
  track,
  player,
  elapsed,
  duration,
  source,
  route,
  onSubmitted,
}) {
  const [review, setReview] = useState({ open, track });
  const [drafts, setDrafts] = useState({});
  const pending = useRef(new Set());
  const mounted = useRef(false);
  const latest = useRef(null);
  const liveTrackId = getRadioFeedbackTrackId(track);
  const previousLiveTrackId = useRef(liveTrackId);

  // Pin on opening only; a queue transition must not switch the review's record.
  if (review.open !== open) {
    setReview({ open, track: open ? track : review.track });
  }
  const trackId = getRadioFeedbackTrackId(review.track);
  const draft = drafts[trackId] || emptyDraft();

  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    latest.current = { open, trackId, liveTrackId, onSubmitted };
  });

  useEffect(() => {
    const changedTrack = previousLiveTrackId.current !== liveTrackId;
    previousLiveTrackId.current = liveTrackId;
    // The transition render can still contain the previous item's playhead.
    if (changedTrack || !open || !trackId || trackId !== liveTrackId) return;
    const evidence = { elapsed, duration };
    setDrafts((current) => {
      const saved = current[trackId] || emptyDraft();
      if (getFeedbackUnlockProgress(evidence) <= getFeedbackUnlockProgress(saved.evidence)) {
        return current;
      }
      // Retain one observed pair, never combine one song's time with another's duration.
      return { ...current, [trackId]: { ...saved, evidence } };
    });
  }, [open, trackId, liveTrackId, elapsed, duration]);

  const updateDraft = (id, fields) => {
    setDrafts((current) => ({
      ...current,
      [id]: { ...(current[id] || emptyDraft()), ...fields },
    }));
  };

  const updateForm = (field, value) => {
    if (pending.current.has(trackId)) return;
    updateDraft(trackId, {
      form: {
        ...draft.form,
        [field]: field === 'comment' ? String(value || '').slice(0, 500) : value,
      },
    });
  };

  const submit = async () => {
    if (
      !player?.token || !trackId || pending.current.has(trackId) || draft.result ||
      !isFeedbackUnlocked(draft.evidence) || !isFeedbackComplete(draft.form)
    ) return;

    pending.current.add(trackId);
    updateDraft(trackId, { submitting: true, error: '', authRequired: false });
    let feedback;
    try {
      const payload = buildRadioFeedbackPayload({
        track: review.track,
        ...draft.evidence,
        feedbackForm: draft.form,
        source,
        route,
      });
      payload.participationListenSessionId = radioOutcomeSession(player.token, trackId);
      const response = await axiosConn.post('hitlab/radio/feedback', payload, {
        headers: { Authorization: `Bearer ${player.token}` },
      });
      feedback = response?.data?.data?.feedback;
      if (
        feedback?.trackId !== trackId ||
        !['credited', 'already_credited', 'pending'].includes(feedback?.rewardStatus)
      ) {
        throw new Error('Feedback response did not confirm the reviewed record.');
      }
      if (!mounted.current) return;
      updateDraft(trackId, { result: feedback, form: initialFeedbackForm, error: '' });
    } catch (error) {
      if (mounted.current) {
        updateDraft(trackId, {
          error: feedbackErrorMessage(error),
          authRequired: error?.response?.status === 401,
        });
      }
      return;
    } finally {
      pending.current.delete(trackId);
      if (mounted.current) updateDraft(trackId, { submitting: false });
    }

    // Only the matching visible review/current Radio item can emit a UI signal.
    const view = latest.current;
    if (
      mounted.current && view?.open &&
      view.trackId === trackId && view.liveTrackId === trackId
    ) {
      view.onSubmitted?.(feedback);
    }
  };

  return { track: review.track, ...draft, updateForm, submit };
}
